import React, { PureComponent } from 'react';
import DatePicker from 'react-datepicker';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { copyObject } from 'utils/utils';
import { withStyles } from '@material-ui/core/styles';
import Input from '@material-ui/core/Input';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import QTypography from 'components/MUIWrappers/QTypography';
import QButton from 'components/QButton';
import { DatePickerHolder } from './styledComponents';

const styles = ({ palette }) => ({
  textField: {
    marginLeft: 0,
    marginRight: 0,
    width: 'inherit',
    '& input': {
      fontSize: 14,
      width: 100,
    },
  },
  regInput: {
    color: 'black',
    paddingTop: 0,
    marginTop: '0px',
    width: '100%',
    height: '20px',
    '& input': {
      padding: '0px',
      paddingBottom: '2px',
      fontSize: '13px',
      '&::-ms-clear ': {
        display: 'none',
      },
    },
  },
  dateButtonStyle: {
    padding: 0,
    height: 25,
    textTransform: 'unset',
  },
  dateButtonLabelStyle: {
    color: palette.common.black,
    fontWeight: 400,
  },
});

export class DateField extends PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      date: moment(props.value),
    };

    this.handleTxDateChange = this.handleTxDateChange.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
  }

  onKeyDown = (e) => {

    const nodes = document.getElementsByClassName('QdatePopper');
    if ((e.key === 'Enter') && nodes.length > 0) {
      e.stopPropagation();
    }

    if (this.props.onKeyDown) {
      this.props.onKeyDown(e, this.props.fieldId);
    }
  };

  setInputRef = (el) => {
    this.textInput = el;
    if (this.props.inputRef) {
      this.props.inputRef(el);
    }
  };

  //------------------------------------------------------
  // handleTxDateChange
  // specific handler for DatePicker
  // converts DatePicker return of a date value into a target.value
  // for standard handling
  // this is needed because the DatePicker component does NOT
  // preserve properties, and returns just the date value to onChange

  handleTxDateChange = (e) => {
    if (this.props.todaysDateIfWrong && !e) {
      return;
    }

    this.setState({ date: e });

    if (e) {
      e.target = this.createTargetFromProps();
      e.target.value = moment(e).format("YYYY-MM-DD");
      if (this.props.onChange) {
        this.props.onChange(e, this.props.fieldId);
      }
    }
  };

  handleFocus = (e) => {
    if (!this.props.disableKeyboardInput) e.target.select();
  };

  //--------------------------------------------------------
  // createTargetFromProps
  //
  // Creates a "data" property if there is one or more
  // data-* properties in this.props (user set data)
  //

  createTargetFromProps = () => {
    const target = copyObject(this.props);
    target.dataset = {};

    Object.keys(this.props).forEach((key) => {
      const p1 = key.slice(0, 5);
      const p2 = key.slice(5);
      if (p1 === 'data-') {
        target.dataset[p2] = this.props[key];
      }
    });
    return target;
  };

  onBlur = (e) => {
    if(this.props.todaysDateIfWrong && !moment(e.target && e.target.value).isValid()) {
      this.handleTxDateChange(moment());
    }

    if (this.props.onBlur) {
      this.props.onBlur(e);
    }
  }

  render() {
    const { classes, editable, value, clickable, className,
      dateFormat, id, showYear, disableKeyboardInput } = this.props;

    const customInput = !disableKeyboardInput ?
      (
        <Input
          disableUnderline={this.props.disableUnderline}
          className={classes.regInput}
          inputProps={{ style: this.props.customStyle }}
          inputRef={this.setInputRef}
        />
      ) : (
        <QButton
          buttonRef={this.setInputRef}
          mini
          classes={{ miniLabel: classes.dateButtonLabelStyle, mini: classes.dateButtonStyle}}
        >
          {moment(value).format(dateFormat || showYear ? 'MMM DD, YYYY' : 'MMM DD, YYYY')}
        </QButton>
      );
    const customInputRef = disableKeyboardInput ? 'buttonRef' : 'inputRef';

    if (editable) {
      return (
        <DatePickerHolder>
          <DatePicker
            id={id}
            key={this.state.date}
            onChange={this.handleTxDateChange}
            selected={this.state.date}
            onFocus={this.handleFocus}
            autoFocus={this.props.autoFocus}
            className={classNames(disableKeyboardInput ? '' : classes.regInput, className)}
            onBlur={this.onBlur}
            popperPlacement="bottom-start"
            popperClassName="QdatePopper"
            onKeyDown={this.onKeyDown}
            popperModifiers={{
              preventOverflow: {
                enabled: true,
                escapeWithReference: true, // force popper to stay in viewport (even when input is scrolled out of view)
                boundariesElement: 'scrollParent',
                priority: ['bottom', 'top', 'left', 'right'],
              },
            }}
            dateFormat={dateFormat}
            customInputRef={customInputRef}
            customInput={customInput}
          >
          </DatePicker>
        </DatePickerHolder>
      );
    }

    return disableKeyboardInput ? customInput : (
      <QTypography
        className={this.props.className}
        style={this.props.customStyle}
        thinFont
        clickable={clickable}
      >
        {moment(value).format(dateFormat || 'MM/DD/YYYY')}
      </QTypography>);
  }
}

DateField.propTypes = {
  value: PropTypes.string,
  className: PropTypes.string,
  id: PropTypes.string,
  onChange: PropTypes.func,
  fieldId: PropTypes.string,
  editable: PropTypes.bool,
  autoFocus: PropTypes.bool,
  clickable: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  inputRef: PropTypes.func,
  onKeyDown: PropTypes.func,
  classes: PropTypes.object,
  customStyle: PropTypes.object,
  disableUnderline: PropTypes.bool,
  dateFormat: PropTypes.string,
  showYear: PropTypes.bool,
  disableKeyboardInput: PropTypes.bool,
  onBlur: PropTypes.func,
  todaysDateIfWrong: PropTypes.bool,
};

export default withStyles(styles)(DateField);
